import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, User, Heart, Menu, X, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import Logo from "@/components/Logo";

const occasions = ["Birthday", "Anniversary", "Wedding", "Valentine's Day"];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false); setSearchQuery("");
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-petal-beige/95 backdrop-blur-md shadow-sm" : "bg-petal-beige/80 backdrop-blur-sm"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Logo className="h-10 md:h-12" testId="navbar-logo" />

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className={`font-outfit text-sm uppercase tracking-widest transition-colors ${isActive("/") ? "text-petal-olive font-medium" : "text-petal-olive-text hover:text-petal-olive"}`}>Home</Link>
              <Link to="/shop" className={`font-outfit text-sm uppercase tracking-widest transition-colors ${isActive("/shop") ? "text-petal-olive font-medium" : "text-petal-olive-text hover:text-petal-olive"}`}>Shop</Link>
              <div className="relative group">
                <button className="font-outfit text-sm uppercase tracking-widest text-petal-olive-text hover:text-petal-olive transition-colors">Occasions</button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white border border-petal-border shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  {occasions.map(occ => (
                    <Link key={occ} to={`/shop?occasion=${encodeURIComponent(occ)}`}
                      className="block px-4 py-2.5 font-outfit text-sm text-petal-olive-text hover:bg-petal-beige-alt hover:text-petal-olive transition-colors">
                      {occ}
                    </Link>
                  ))}
                </div>
              </div>
              <Link to="/about" className={`font-outfit text-sm uppercase tracking-widest transition-colors ${isActive("/about") ? "text-petal-olive font-medium" : "text-petal-olive-text hover:text-petal-olive"}`}>About</Link>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-4">
              <button onClick={() => setSearchOpen(!searchOpen)} data-testid="search-btn" className="text-petal-olive-text hover:text-petal-olive transition-colors">
                <Search className="w-5 h-5" />
              </button>
              <Link to="/cart" data-testid="cart-btn" className="relative text-petal-olive-text hover:text-petal-olive transition-colors">
                <ShoppingBag className="w-5 h-5" />
                {cart.item_count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-petal-gold text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-outfit font-medium">
                    {cart.item_count}
                  </span>
                )}
              </Link>
              {user ? (
                <div className="relative group hidden md:block">
                  <button className="flex items-center gap-1 text-petal-olive-text hover:text-petal-olive transition-colors" data-testid="account-btn">
                    <User className="w-5 h-5" />
                  </button>
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-petal-border shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="px-4 py-3 border-b border-petal-border">
                      <p className="font-outfit text-sm font-medium text-petal-olive-text truncate">{user.name}</p>
                      <p className="font-outfit text-xs text-petal-muted truncate">{user.email}</p>
                    </div>
                    <Link to="/dashboard" className="block px-4 py-2.5 font-outfit text-sm text-petal-olive-text hover:bg-petal-beige-alt transition-colors">My Dashboard</Link>
                    {user.role === "admin" && (
                      <Link to="/admin" className="block px-4 py-2.5 font-outfit text-sm text-petal-olive hover:bg-petal-beige-alt transition-colors font-medium">Admin Panel</Link>
                    )}
                    <button onClick={handleLogout} data-testid="logout-btn" className="w-full text-left px-4 py-2.5 font-outfit text-sm text-red-500 hover:bg-petal-beige-alt transition-colors">Logout</button>
                  </div>
                </div>
              ) : (
                <Link to="/login" data-testid="login-btn" className="hidden md:flex btn-primary text-xs py-2 px-5">Login</Link>
              )}
              <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-petal-olive-text" data-testid="mobile-menu-btn">
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <div className="pb-3 border-t border-petal-border">
              <form onSubmit={handleSearch} className="flex gap-2 pt-3">
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search gifts..." data-testid="search-input"
                  className="flex-1 px-4 py-2 border border-petal-border bg-white font-outfit text-sm focus:outline-none focus:border-petal-olive" autoFocus />
                <button type="submit" className="btn-primary text-xs py-2 px-4">Search</button>
              </form>
            </div>
          )}
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-petal-beige border-t border-petal-border px-4 py-4 space-y-3">
            {[{to:"/",label:"Home"},{to:"/shop",label:"Shop"},{to:"/about",label:"About"}].map(l => (
              <Link key={l.to} to={l.to} className="block font-outfit text-sm uppercase tracking-widest text-petal-olive-text hover:text-petal-olive py-2">{l.label}</Link>
            ))}
            <div className="border-t border-petal-border pt-3 space-y-2">
              {occasions.map(occ => (
                <Link key={occ} to={`/shop?occasion=${encodeURIComponent(occ)}`}
                  className="block font-outfit text-sm text-petal-muted hover:text-petal-olive py-1.5">{occ}</Link>
              ))}
            </div>
            {user ? (
              <div className="border-t border-petal-border pt-3 space-y-2">
                <Link to="/dashboard" className="block font-outfit text-sm text-petal-olive-text py-1.5">My Dashboard</Link>
                {user.role === "admin" && <Link to="/admin" className="block font-outfit text-sm text-petal-olive py-1.5 font-medium">Admin Panel</Link>}
                <button onClick={handleLogout} className="block font-outfit text-sm text-red-500 py-1.5">Logout</button>
              </div>
            ) : (
              <Link to="/login" className="block btn-primary text-center text-xs py-3 mt-2">Login / Register</Link>
            )}
          </div>
        )}
      </nav>
      <div className="h-16 md:h-20" />
    </>
  );
}
